import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, GripVertical, BookMarked, X, Dumbbell } from "lucide-react";

export function SortablePeriod({ p, idx, plays, drills, onChange, onRemove, disabled }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: `period:${p.id}`, disabled });

  // Also act as a drop target for plays
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `period-drop:${p.id}`,
    data: { type: "period", periodId: p.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const linkedPlays = (p.play_ids || []).map(pid => plays.find(pl => pl.id === pid)).filter(Boolean);
  const linkedDrills = (p.drill_ids || []).map(did => drills.find(dr => dr.id === did)).filter(Boolean);

  const removePlayId = (pid) => onChange({ ...p, play_ids: (p.play_ids || []).filter(x => x !== pid) });
  const removeDrillId = (did) => onChange({ ...p, drill_ids: (p.drill_ids || []).filter(x => x !== did) });

  const setRefs = (node) => { setNodeRef(node); setDropRef(node); };

  return (
    <div
      ref={setRefs}
      style={style}
      data-testid={`period-row-${idx}`}
      className={`border-b border-surface-3 py-2 pl-2 pr-2 -mx-2 transition-colors ${
        isOver ? "bg-amber/10 border-l-4 border-l-amber" : "hover:bg-surface-2 border-l-4 border-l-transparent"
      }`}
    >
      <div className="grid grid-cols-[auto_60px_2fr_1.5fr_1.5fr_2fr_auto] items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="text-slate-500 hover:text-amber cursor-grab active:cursor-grabbing p-1"
          data-testid={`period-drag-${idx}`}
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Input type="number" min={1} value={p.duration} onChange={(e)=>onChange({...p, duration:+e.target.value})}
          className="bg-background border-surface-3 h-9 rounded-none text-center font-mono" data-testid={`period-duration-${idx}`} />
        <Input value={p.name} onChange={(e)=>onChange({...p, name:e.target.value})} placeholder="Period name"
          className="bg-background border-surface-3 h-9 rounded-none text-sm" data-testid={`period-name-${idx}`} />
        <Input value={p.coach} onChange={(e)=>onChange({...p, coach:e.target.value})} placeholder="Coach"
          className="bg-background border-surface-3 h-9 rounded-none text-sm" data-testid={`period-coach-${idx}`} />
        <Input value={p.equipment} onChange={(e)=>onChange({...p, equipment:e.target.value})} placeholder="Equipment"
          className="bg-background border-surface-3 h-9 rounded-none text-sm" />
        <Input value={p.notes} onChange={(e)=>onChange({...p, notes:e.target.value})} placeholder="Notes"
          className="bg-background border-surface-3 h-9 rounded-none text-sm" />
        <Button variant="ghost" onClick={onRemove} className="rounded-none hover:text-red-400 h-9 w-9 p-0" data-testid={`period-remove-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Linked plays + drills */}
      <div className="pl-[calc(24px+60px+12px)] mt-1.5 flex flex-wrap items-center gap-1" data-testid={`period-plays-${idx}`}>
        {linkedPlays.length === 0 && linkedDrills.length === 0 && !isOver && (
          <span className="text-[9px] font-condensed uppercase tracking-widest text-slate-600 italic">
            Drag a play or drill here to link it →
          </span>
        )}
        {isOver && (
          <span className="text-[10px] font-condensed uppercase tracking-widest text-amber animate-pulse">
            Drop to link
          </span>
        )}
        {linkedPlays.map(pl => (
          <span key={pl.id} data-testid={`period-play-${pl.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-condensed uppercase tracking-widest bg-amber/10 text-amber border border-amber/40 px-1.5 py-0.5">
            <BookMarked className="w-3 h-3" /> {pl.name}
            <button onClick={() => removePlayId(pl.id)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {linkedDrills.map(dr => (
          <span key={dr.id} data-testid={`period-drill-${dr.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-condensed uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5">
            <Dumbbell className="w-3 h-3" /> {dr.name}
            <button onClick={() => removeDrillId(dr.id)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}
